/**
 * cache/loot.ts — what a cleared cache pays out.
 *
 * A tiered table keyed by (theme, tier) → credits + materials + a cosmetic-unlock roll.
 * Rolled SERVER-SIDE on boss death (P5b) and granted through the authoritative
 * inventory; nothing here is ever rolled by a client for its own reward.
 *
 * ANTI-SCUM. The roll is seeded `rngFromString("<site>:<epoch>:loot")` — the same two
 * values that seed the dungeon itself. The server owns epochs (server/src/cache.ts mints
 * one per instance and holds it for the run + a grace window), so re-entering the SAME
 * instance re-derives the SAME prize. There is no roll to scum; to see a different prize
 * you have to earn a different epoch.
 *
 * REPEAT RUNS. `firstClear: false` is the haircut, not a lockout — a repeat run still
 * plays and still pays, at a quarter credits and half materials, with no cosmetic. The
 * haircut is applied AFTER the roll, from the same stream, so a repeat is provably the
 * first-clear prize scaled rather than a separate draw.
 *
 * SHARED-CODE DISCIPLINE: no trig, no `Math.random`, no `Date`, no `**` — the pmath-ban
 * gate covers this directory.
 */
import { rangeInt, rngFromString, type Rng } from "../rng.js";
import { DEFAULT_STYLE, ITEMS, STYLE_PARTS, STYLE_VARIANTS } from "../rpg.js";
import type { CacheTheme } from "./types.js";

export interface CacheLootItem {
  id: string;
  n: number;
}

export interface CacheLoot {
  credits: number;
  items: CacheLootItem[];
  /** A `"<part>:<variant>"` cosmetic id, or null when the roll missed. */
  unlock: string | null;
}

export interface CacheLootInput {
  site: string;
  epoch: number;
  theme: CacheTheme;
  tier: number;
  /** False = a repeat run at this site: the haircut applies. */
  firstClear: boolean;
}

/** One material line in a table entry. */
export interface LootMaterial {
  /** MUST be a real id in the ITEMS catalog — enforced at module load. */
  id: string;
  /** Relative weight for the without-replacement draw. */
  w: number;
  /** Inclusive stack-size band for this drop. */
  min: number;
  max: number;
}

export interface LootTier {
  /** Inclusive credit band. */
  credits: readonly [number, number];
  /** Inclusive count of DISTINCT material lines drawn (clamped to the pool size). */
  drops: readonly [number, number];
  materials: readonly LootMaterial[];
}

/**
 * The payout table. Index 0 = tier 0 (a 3–5 minute run), index 1 = tier 1+ (10–15).
 *
 * Themes pay in their own currency of flavour: a `vault` is where the money is, a
 * `hollow` pays in raw ore you had to dig for, a `ruin` in salvage. Tier roughly triples
 * credits and widens the material bands — a long run must be worth more than three short
 * ones or nobody runs it.
 */
export const LOOT_TABLE: Record<CacheTheme, readonly LootTier[]> = {
  ruin: [
    {
      credits: [180, 420],
      drops: [2, 3],
      materials: [
        { id: "scrap-alloy", w: 40, min: 3, max: 9 },
        { id: "ore-ferrite", w: 32, min: 2, max: 7 },
        { id: "ore-volatite", w: 16, min: 1, max: 4 },
        { id: "exotic-crystal", w: 8, min: 1, max: 2 },
      ],
    },
    {
      credits: [520, 1100],
      drops: [3, 4],
      materials: [
        { id: "scrap-alloy", w: 34, min: 8, max: 20 },
        { id: "ore-ferrite", w: 30, min: 6, max: 16 },
        { id: "ore-volatite", w: 20, min: 4, max: 12 },
        { id: "exotic-crystal", w: 14, min: 2, max: 6 },
      ],
    },
  ],
  vault: [
    {
      credits: [240, 520],
      drops: [2, 3],
      materials: [
        { id: "exotic-crystal", w: 26, min: 1, max: 3 },
        { id: "scrap-alloy", w: 30, min: 2, max: 8 },
        { id: "ore-ferrite", w: 26, min: 2, max: 6 },
        { id: "fuel-cell", w: 12, min: 1, max: 2 },
      ],
    },
    {
      credits: [680, 1400],
      drops: [3, 4],
      materials: [
        { id: "exotic-crystal", w: 30, min: 3, max: 8 },
        { id: "scrap-alloy", w: 24, min: 6, max: 16 },
        { id: "ore-ferrite", w: 22, min: 5, max: 14 },
        { id: "fuel-cell", w: 18, min: 1, max: 4 },
      ],
    },
  ],
  hollow: [
    {
      credits: [150, 360],
      drops: [2, 3],
      materials: [
        { id: "ore-ferrite", w: 34, min: 3, max: 9 },
        { id: "ore-cryonite", w: 28, min: 2, max: 7 },
        { id: "ore-volatite", w: 24, min: 2, max: 7 },
        { id: "exotic-crystal", w: 10, min: 1, max: 2 },
      ],
    },
    {
      credits: [460, 980],
      drops: [3, 4],
      materials: [
        { id: "ore-ferrite", w: 30, min: 8, max: 20 },
        { id: "ore-cryonite", w: 26, min: 6, max: 16 },
        { id: "ore-volatite", w: 26, min: 6, max: 16 },
        { id: "exotic-crystal", w: 16, min: 2, max: 6 },
      ],
    },
  ],
};

/**
 * Every cosmetic a cache can unlock: EXISTING `STYLE_VARIANTS` ids, formatted
 * `"<part>:<variant>"`, minus each part's default variant (`DEFAULT_STYLE` — the empty
 * slot, which every pilot already has and which "unlocking" would be a non-event).
 *
 * Derived, never hand-listed: adding a variant to the wardrobe adds it to the drop pool
 * with no edit here. No new art is a cache V1 dependency, by design.
 */
export const CACHE_UNLOCK_POOL: readonly string[] = (() => {
  const out: string[] = [];
  for (const part of STYLE_PARTS) {
    for (const variant of STYLE_VARIANTS[part]) {
      if (variant === "none" || variant === DEFAULT_STYLE[part]) continue;
      out.push(`${part}:${variant}`);
    }
  }
  return out;
})();

/** Chance of a cosmetic unlock on a FIRST clear, by tier. Repeat runs roll 0. */
export const CACHE_UNLOCK_CHANCE = [0.08, 0.15] as const;

/** Repeat-run haircut. Credits floor, materials ceil (a repeat never pays zero of a line). */
export const REPEAT_CREDIT_MULT = 0.25;
export const REPEAT_MATERIAL_MULT = 0.5;

/**
 * Table validation, run at module load below.
 *
 * A material id that is not in the ITEMS catalog is a typo that would otherwise surface
 * as a silently-dropped grant on some player's boss kill weeks later (`validateInventory`
 * drops unknown ids by design — rpg.ts). Failing at import instead makes it a build-time
 * error for everyone, immediately. Exported so tests can exercise it against a bad table
 * without mutating the real one.
 */
export function assertLootTableItems(table: Record<string, readonly LootTier[]>): void {
  for (const theme of Object.keys(table)) {
    const tiers = table[theme];
    for (let t = 0; t < tiers.length; t++) {
      const entry = tiers[t];
      if (entry.credits[0] < 0 || entry.credits[1] < entry.credits[0]) {
        throw new Error(`cache loot table ${theme}[${t}]: bad credit band`);
      }
      if (entry.materials.length === 0) {
        throw new Error(`cache loot table ${theme}[${t}]: no materials`);
      }
      for (const m of entry.materials) {
        const def = ITEMS[m.id];
        if (!def) throw new Error(`cache loot table ${theme}[${t}]: unknown item id "${m.id}"`);
        if (def.kind !== "material") {
          throw new Error(`cache loot table ${theme}[${t}]: "${m.id}" is a ${def.kind}, not a material`);
        }
        if (m.w <= 0 || m.min < 1 || m.max < m.min) {
          throw new Error(`cache loot table ${theme}[${t}]: bad band on "${m.id}"`);
        }
      }
    }
  }
}

assertLootTableItems(LOOT_TABLE);

/** The table entry for a (theme, tier), tier clamped to the entries that exist. */
export function lootTierFor(theme: CacheTheme, tier: number): LootTier {
  const tiers = LOOT_TABLE[theme];
  const t = Math.min(tiers.length - 1, Math.max(0, Math.floor(tier)));
  return tiers[t];
}

/** Unlock chance for a tier, clamped the same way. */
export function unlockChance(tier: number): number {
  const t = Math.min(CACHE_UNLOCK_CHANCE.length - 1, Math.max(0, Math.floor(tier)));
  return CACHE_UNLOCK_CHANCE[t];
}

/**
 * Roll one cache's payout.
 *
 * The draw order is FIXED and independent of `firstClear`: credits, then the material
 * lines, then the unlock roll. The haircut is applied to the result, never to the stream,
 * so `rollLoot({…, firstClear: false })` is exactly the first-clear prize scaled — which
 * is a property a test can assert, and a property a player can be told.
 */
export function rollLoot(input: CacheLootInput): CacheLoot {
  const rng: Rng = rngFromString(`${input.site}:${input.epoch}:loot`);
  const entry = lootTierFor(input.theme, input.tier);

  const credits0 = rangeInt(rng, entry.credits[0], entry.credits[1]);

  const wanted = Math.min(
    entry.materials.length,
    rangeInt(rng, entry.drops[0], entry.drops[1]),
  );
  const remaining = [...entry.materials];
  const items: CacheLootItem[] = [];
  for (let k = 0; k < wanted; k++) {
    const m = takeWeighted(rng, remaining);
    if (!m) break;
    items.push({ id: m.id, n: rangeInt(rng, m.min, m.max) });
  }

  // Both unlock draws happen unconditionally so the stream stays aligned between a first
  // clear and a repeat; only the ACCEPTANCE is gated.
  const unlockRoll = rng();
  const unlockIndex = Math.floor(rng() * CACHE_UNLOCK_POOL.length);

  if (input.firstClear) {
    return {
      credits: credits0,
      items,
      unlock: unlockRoll < unlockChance(input.tier) ? CACHE_UNLOCK_POOL[unlockIndex] : null,
    };
  }
  return {
    credits: Math.floor(credits0 * REPEAT_CREDIT_MULT),
    items: items.map((it) => ({ id: it.id, n: Math.ceil(it.n * REPEAT_MATERIAL_MULT) })),
    unlock: null,
  };
}

/** Weighted draw WITHOUT replacement — removes and returns the chosen line. */
function takeWeighted(rng: Rng, pool: LootMaterial[]): LootMaterial | null {
  if (pool.length === 0) return null;
  let total = 0;
  for (const m of pool) total += m.w;
  let r = rng() * total;
  let chosen = pool.length - 1; // float-safe fallback
  for (let i = 0; i < pool.length; i++) {
    r -= pool[i].w;
    if (r < 0) { chosen = i; break; }
  }
  return pool.splice(chosen, 1)[0];
}
